import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  IconButton,
  Chip,
  Box,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Card,
  CardContent,
  CardActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  FormHelperText
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  HelpOutline as HelpOutlineIcon
} from '@mui/icons-material';
import keycloak from '../keycloak';
import { ROLES } from '../auth/roles';
import { hasRoleOrGroup } from '../auth/access';
import Service from '../services/Service';
import { useRef } from 'react';

// Componente para editar um filtro individual
function FilterRuleEditor({ filter, index, onUpdate, onDelete }) {
  const [localFilter, setLocalFilter] = useState(filter);
  const [isEditing, setIsEditing] = useState(false);
  const [inclusaoInput, setInclusaoInput] = useState('');
  const [exclusaoInput, setExclusaoInput] = useState('');
  const [inclusaoError, setInclusaoError] = useState('');
  const [exclusaoError, setExclusaoError] = useState('');
  const inclusaoFileInputRef = useRef(null);
  const exclusaoFileInputRef = useRef(null);

  useEffect(() => {
    setLocalFilter(filter);
  }, [filter]);

  const handleChange = (field) => (event) => {
    const newFilter = {
      ...localFilter,
      [field]: event.target.value
    };
    setLocalFilter(newFilter);
    onUpdate(index, newFilter);
  };

  const handleTipoChange = (event) => {
    const newFilter = {
      ...localFilter,
      tipo: event.target.value,
      // Reset targetColumn se não for COLUNA_ESPECIFICA
      targetColumn: event.target.value === 'COLUNA_ESPECIFICA' ? localFilter.targetColumn || '' : null
    };
    setLocalFilter(newFilter);
    onUpdate(index, newFilter);
  };

  const handleAddInclusao = () => {
    const trimmedValue = inclusaoInput; //.trim();

    if (!trimmedValue) {
      return;
    }

    const currentInclusao = localFilter.criteria?.inclusao || [];
    const alreadyExists = currentInclusao.some((keyword) => keyword.toLowerCase() === trimmedValue.toLowerCase());

    if (alreadyExists) {
      setInclusaoError('Essa palavra-chave já foi adicionada.');
      return;
    }

    const newFilter = {
      ...localFilter,
      criteria: {
        ...localFilter.criteria,
        inclusao: [...currentInclusao, trimmedValue]
      }
    };

    setLocalFilter(newFilter);
    onUpdate(index, newFilter);
    setInclusaoInput('');
    setInclusaoError('');
  };

  const handleRemoveInclusao = (keywordToRemove) => {
    const newFilter = {
      ...localFilter,
      criteria: {
        ...localFilter.criteria,
        inclusao: (localFilter.criteria?.inclusao || []).filter(k => k !== keywordToRemove)
      }
    };
    setLocalFilter(newFilter);
    onUpdate(index, newFilter);
  };

  const handleAddExclusao = () => {
    const trimmedValue = exclusaoInput; //.trim();

    if (!trimmedValue) {
      return;
    }

    const currentExclusao = localFilter.criteria?.exclusao || [];
    const alreadyExists = currentExclusao.some((keyword) => keyword.toLowerCase() === trimmedValue.toLowerCase());

    if (alreadyExists) {
      setExclusaoError('Essa palavra-chave já foi adicionada.');
      return;
    }

    const newFilter = {
      ...localFilter,
      criteria: {
        ...localFilter.criteria,
        exclusao: [...currentExclusao, trimmedValue]
      }
    };

    setLocalFilter(newFilter);
    onUpdate(index, newFilter);
    setExclusaoInput('');
    setExclusaoError('');
  };

  const handleRemoveExclusao = (keywordToRemove) => {
    const newFilter = {
      ...localFilter,
      criteria: {
        ...localFilter.criteria,
        exclusao: (localFilter.criteria?.exclusao || []).filter(k => k !== keywordToRemove)
      }
    };
    setLocalFilter(newFilter);
    onUpdate(index, newFilter);
  };

  const parseCsvKeywords = (text) => {
    if (!text || !text.trim()) {
      return [];
    }

    const rows = text
      .split(/\r?\n/)
      .map((row) => row)
      .filter(Boolean);

    if (rows.length === 0) {
      return [];
    }

    const hasHeader = rows[0].toLowerCase().includes('palavra') || rows[0].toLowerCase().includes('keyword');
    const dataRows = hasHeader ? rows.slice(1) : rows;

    return dataRows
      .map((row) => {
        const [firstCol] = row.split(',').map((value) => value);
        return firstCol.replace(/^"(.*)"$/, '$1').replace(/""/g, '"');
      })
      .filter(Boolean);
  };

  const buildCsvContent = (keywords = []) => {
    const header = 'palavra';
    const rows = keywords.map((keyword) => `"${String(keyword).replace(/"/g, '""')}"`);
    return [header, ...rows].join('\n');
  };

  const downloadCsv = (keywords, filename) => {
    const content = buildCsvContent(keywords);
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportarInclusao = () => {
    const keywords = localFilter.criteria?.inclusao || [];
    downloadCsv(keywords, `${(localFilter.nome || 'filtro').replace(/\s+/g, '-').toLowerCase()}-inclusao.csv`);
  };

  const handleExportarExclusao = () => {
    const keywords = localFilter.criteria?.exclusao || [];
    downloadCsv(keywords, `${(localFilter.nome || 'filtro').replace(/\s+/g, '-').toLowerCase()}-exclusao.csv`);
  };

  const handleImportarInclusao = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const content = await file.text();
      const importedKeywords = parseCsvKeywords(content);

      if (importedKeywords.length === 0) {
        setInclusaoError('O arquivo CSV não contém palavras-chave válidas.');
        return;
      }

      const currentInclusao = localFilter.criteria?.inclusao || [];
      const uniqueKeywords = importedKeywords.filter((keyword) => !currentInclusao.some((existing) => existing.toLowerCase() === keyword.toLowerCase()));

      const newFilter = {
        ...localFilter,
        criteria: {
          ...localFilter.criteria,
          inclusao: [...currentInclusao, ...uniqueKeywords]
        }
      };

      setLocalFilter(newFilter);
      onUpdate(index, newFilter);
      setInclusaoError(uniqueKeywords.length < importedKeywords.length ? 'Algumas palavras-chave já existiam e foram ignoradas.' : '');
      event.target.value = '';
    } catch (error) {
      console.error(error);
      setInclusaoError('Não foi possível importar o CSV.');
    }
  };

  const handleImportarExclusao = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const content = await file.text();
      const importedKeywords = parseCsvKeywords(content);

      if (importedKeywords.length === 0) {
        setExclusaoError('O arquivo CSV não contém palavras-chave válidas.');
        return;
      }

      const currentExclusao = localFilter.criteria?.exclusao || [];
      const uniqueKeywords = importedKeywords.filter((keyword) => !currentExclusao.some((existing) => existing.toLowerCase() === keyword.toLowerCase()));

      const newFilter = {
        ...localFilter,
        criteria: {
          ...localFilter.criteria,
          exclusao: [...currentExclusao, ...uniqueKeywords]
        }
      };

      setLocalFilter(newFilter);
      onUpdate(index, newFilter);
      setExclusaoError(uniqueKeywords.length < importedKeywords.length ? 'Algumas palavras-chave já existiam e foram ignoradas.' : '');
      event.target.value = '';
    } catch (error) {
      console.error(error);
      setExclusaoError('Não foi possível importar o CSV.');
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            {localFilter.nome || `Filtro ${index + 1}`}
          </Typography>
          <IconButton
            color="error"
            onClick={() => onDelete(index)}
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nome do Filtro"
              value={localFilter.nome || ''}
              onChange={handleChange('nome')}
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo do Filtro</InputLabel>
                <Select
                  value={localFilter.tipo || 'SIMPLES'}
                  onChange={handleTipoChange}
                  label="Tipo do Filtro"
                >
                  <MenuItem value="SIMPLES">
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body2">Simples</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="EXCLUSIVO">
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body2">Exclusivo</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="COLUNA_ESPECIFICA">
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body2">Coluna Específica</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="DESATIVO">
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body2">Desativo</Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
              <Tooltip
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Tipos de Filtro:
                    </Typography>
                    <Typography variant="body2">
                      <strong>Simples:</strong> Itens que contenham as palavras de inclusão
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Exclusivo:</strong> Itens que passaram em outro filtro e passa nessa clasificação são absorvido para esse filtro, mesmo que ele não tenha prioridade.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Coluna Específica:</strong> Busca apenas em uma coluna específica
                      do documento
                    </Typography>
                  </Box>
                }
                placement="right"
                arrow
              >
                <IconButton size="small" sx={{ ml: 0.5 }}>
                  <HelpOutlineIcon fontSize="small" color="action" />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <TextField
                fullWidth
                label="Prioridade"
                type="number"
                value={localFilter.prioridade || ''}
                onChange={handleChange('prioridade')}
                size="small"
                InputProps={{
                  inputProps: { min: 0, max: 100 }
                }}
              // helperText="Quanto menor o número, maior a prioridade"
              />
              <Tooltip
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography variant="body2">
                      Quanto menor o número, maior a prioridade"
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                      Os itens são ordenados pela prioridade do filtro que os capturou
                    </Typography>
                  </Box>
                }
                placement="right"
                arrow
              >
                <IconButton size="small">
                  <HelpOutlineIcon fontSize="small" color="action" />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>

          {localFilter.tipo === 'COLUNA_ESPECIFICA' && (
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Coluna Alvo</InputLabel>
                <Select
                  value={localFilter.targetColumn || ''}
                  onChange={handleChange('targetColumn')}
                  label="Coluna Alvo"
                >
                  <MenuItem value="">
                    <em>Nenhuma (buscar em todas as colunas)</em>
                  </MenuItem>
                  <Divider />
                  <MenuItem value="uf">
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body2">UF</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="grupo">
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body2">Grupos</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="subGrupo">
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body2">SubGrupos</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="orgaos">
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body2">Órgãos</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="unidade">
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body2">Unidade</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="cidadeUf">
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body2">Cidade-UF</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="tipo">
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body2">Tipos</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="numero">
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body2">Número</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="dataAbertura">
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body2">Data de abertura</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="horaAbertura">
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body2">Hora de abertura</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="objeto">
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body2">Objeto</Typography>
                    </Box>
                  </MenuItem>
                </Select>
                <FormHelperText>
                  Selecione a coluna específica onde a busca será aplicada
                </FormHelperText>
              </FormControl>
            </Grid>
          )}

          {/* <Grid item xs={12}>
            <TextField
              fullWidth
              label="Seção de Saída"
              value={localFilter.outputSection || ''}
              onChange={handleChange('outputSection')}
              size="small"
              placeholder="Ex: Notebooks Gamer, 🔥 OFERTAS 🔥"
              helperText="Nome da seção no relatório de email"
            />
          </Grid> */}

          {/* <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={localFilter.sendSeparateEmail || false}
                  onChange={(e) => handleChange('sendSeparateEmail')({ target: { value: e.target.checked } })}
                  size="small"
                />
              }
              label="Enviar em email separado"
            />
          </Grid> */}

          <Grid item xs={12}>
            <Divider />
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Palavras-chave de INCLUSÃO
            </Typography>
            <Box display="flex" gap={1} mb={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Adicionar palavra-chave"
                value={inclusaoInput}
                onChange={(e) => {
                  setInclusaoInput(e.target.value);
                  if (inclusaoError) {
                    setInclusaoError('');
                  }
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleAddInclusao()}
              />
              <Button
                variant="outlined"
                component="label"
                size="small"
              >
                Importar
                <input
                  type="file"
                  accept=".csv,text/csv"
                  hidden
                  ref={inclusaoFileInputRef}
                  onChange={handleImportarInclusao}
                />
              </Button>
              <Button
                variant="outlined"
                onClick={handleExportarInclusao}
                size="small"
              >
                Exportar
              </Button>
              <Button
                variant="outlined"
                onClick={handleAddInclusao}
                size="small"
              >
                Adicionar
              </Button>
            </Box>
            <Box sx={{ minHeight: 40 }}>
              {(localFilter.criteria?.inclusao || []).map((keyword, idx) => (
                <Chip
                  key={idx}
                  label={<span style={{ whiteSpace: 'pre-wrap' }}>{keyword}</span>}
                  onDelete={() => handleRemoveInclusao(keyword)}
                  size="small"
                  sx={{ m: 0.5 }}
                  color="primary"
                />
              ))}
              {(!localFilter.criteria?.inclusao || localFilter.criteria.inclusao.length === 0) && (
                <Typography color="textSecondary" variant="caption">
                  Nenhuma palavra-chave de inclusão
                </Typography>
              )}
            </Box>
            {inclusaoError && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {inclusaoError}
              </Alert>
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
              Palavras-chave de EXCLUSÃO
            </Typography>
            <Box display="flex" gap={1} mb={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Adicionar palavra-chave"
                value={exclusaoInput}
                onChange={(e) => {
                  setExclusaoInput(e.target.value);
                  if (exclusaoError) {
                    setExclusaoError('');
                  }
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleAddExclusao()}
              />
              <Button
                variant="outlined"
                component="label"
                size="small"
              >
                Importar
                <input
                  type="file"
                  accept=".csv,text/csv"
                  hidden
                  ref={exclusaoFileInputRef}
                  onChange={handleImportarExclusao}
                />
              </Button>
              <Button
                variant="outlined"
                onClick={handleExportarExclusao}
                size="small"
              >
                Exportar
              </Button>
              <Button
                variant="outlined"
                onClick={handleAddExclusao}
                size="small"
              >
                Adicionar
              </Button>
            </Box>
            <Box sx={{ minHeight: 40 }}>
              {(localFilter.criteria?.exclusao || []).map((keyword, idx) => (
                <Chip
                  key={idx}
                  label={<span style={{ whiteSpace: 'pre-wrap' }}>{keyword}</span>}
                  onDelete={() => handleRemoveExclusao(keyword)}
                  size="small"
                  sx={{ m: 0.5 }}
                  color="error"
                />
              ))}
              {(!localFilter.criteria?.exclusao || localFilter.criteria.exclusao.length === 0) && (
                <Typography color="textSecondary" variant="caption">
                  Nenhuma palavra-chave de exclusão
                </Typography>
              )}
            </Box>
            {exclusaoError && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {exclusaoError}
              </Alert>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

// Componente principal do Dialog
export default function ConfigUserFormDialog(props) {
  const { open, onClose, userData, setWait, onSaveSuccess, handleExport } = props;
  const isEditing = !!userData;

  const [formData, setFormData] = useState({
    nome: '',
    urlRef: '',
    emails: [],
    filtros: [],
    active: true
  });

  const [newEmail, setNewEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [expandedFilter, setExpandedFilter] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const filtrosContainerRef = useRef(null);
  const ultimoFiltroRef = useRef(null);

  useEffect(() => {
    if (userData) {
      setFormData({
        nome: userData.nome || '',
        urlRef: userData.urlRef || '',
        emails: userData.emails || [],
        filtros: userData.filtros || [],
        active: userData.active !== undefined ? userData.active : true
      });
    } else {
      // Reset form for new item.
      // Usuários com a role "user" só enxergam/criam os próprios registros
      // (regra aplicada de verdade no backend), então já deixamos o próprio
      // email preenchido aqui — ele ainda pode adicionar outros emails.
      const isAdmin = hasRoleOrGroup(ROLES.ADMIN);
      const meuEmail = keycloak.tokenParsed?.email;
      setFormData({
        nome: '',
        urlRef: 'https://www.licinet.com.br/xml/planilhaEmailDoClientePNCP.php?edicao=Licinet%20TURNO%20DATA&cliente=62745&nome=Very%20Tecnologia%20Ltda%20&email=licitacoes@verytecnologia.com.br',
        emails: !isAdmin && meuEmail ? [meuEmail] : [],
        filtros: [],
        active: true
      });
    }
    setErrors({});
    setExpandedFilter(null);
  }, [userData, open]);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: null
      });
    }
  };

  const handleAddEmail = () => {
    if (newEmail && !formData.emails.includes(newEmail)) {
      setFormData({
        ...formData,
        emails: [...formData.emails, newEmail]
      });
      setNewEmail('');
    }
  };

  const handleRemoveEmail = (emailToRemove) => {
    setFormData({
      ...formData,
      emails: formData.emails.filter(email => email !== emailToRemove)
    });
  };

  // Funções para gerenciar filtros
  const handleAddFilter = () => {
    const newFilter = {
      nome: `Novo Filtro ${formData.filtros.length + 1}`,
      tipo: 'SIMPLES',
      prioridade: formData.filtros.length + 1,
      criteria: {
        inclusao: [],
        exclusao: []
      },
      targetColumn: null,
      sendSeparateEmail: false,
      outputSection: ''
    };

    setFormData({
      ...formData,
      filtros: [...formData.filtros, newFilter]
    });

    // Expandir o novo filtro
    setExpandedFilter(formData.filtros.length);

    // Rolar para o novo filtro após o render
    setTimeout(() => {
      if (ultimoFiltroRef.current) {
        ultimoFiltroRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  const handleUpdateFilter = (index, updatedFilter) => {
    const newFiltros = [...formData.filtros];
    newFiltros[index] = updatedFilter;
    setFormData({
      ...formData,
      filtros: newFiltros
    });
  };

  const handleDeleteFilter = (index) => {
    const newFiltros = formData.filtros.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      filtros: newFiltros
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nome || formData.nome === '') { //  if (!formData.nome || formData.nome.trim() === '') {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.urlRef || formData.urlRef === '') { // if (!formData.urlRef || formData.urlRef.trim() === '') {
      newErrors.urlRef = 'URL é obrigatória';
    }

    if (formData.emails.length === 0) {
      newErrors.emails = 'Adicione pelo menos um email';
    }

    // Validar filtros
    if (formData.filtros.length === 0) {
      newErrors.filtros = 'Adicione pelo menos um filtro';
    } else {
      formData.filtros.forEach((filtro, idx) => {
        if (!filtro.nome || filtro.nome === '') { // if (!filtro.nome || filtro.nome.trim() === '') {
          newErrors[`filtro_${idx}_nome`] = `Filtro ${idx + 1}: Nome é obrigatório`;
        }
        if (filtro.tipo === 'COLUNA_ESPECIFICA' && (!filtro.targetColumn || filtro.targetColumn === '')) { //         if (filtro.tipo === 'COLUNA_ESPECIFICA' && (!filtro.targetColumn || filtro.targetColumn.trim() === '')) {
          newErrors[`filtro_${idx}_target`] = `Filtro ${idx + 1}: Coluna alvo é obrigatória para tipo COLUNA_ESPECIFICA`;
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    let payload = {
      nome: formData.nome,
      urlRef: formData.urlRef,
      emails: formData.emails,
      filtros: formData.filtros.map(filtro => ({
        nome: filtro.nome,
        tipo: filtro.tipo,
        prioridade: parseInt(filtro.prioridade) || 0,
        criteria: {
          inclusao: filtro.criteria?.inclusao || [],
          exclusao: filtro.criteria?.exclusao || []
        },
        targetColumn: filtro.targetColumn || null,
        sendSeparateEmail: filtro.sendSeparateEmail || false,
        outputSection: filtro.outputSection || ''
      })),
      active: formData.active
    };
    if (userData?.id) {
      payload.id = userData.id;
    }

    setLoading(true);
    Service.POST('/api/configUser', payload).then(resp => {
      setLoading(false);
      if (resp) {
        // console.log(">>>salve<<<", resp)
        if (onSaveSuccess) onSaveSuccess();
        onClose();
      }
    }).catch(error => {
      console.error(error);
      setLoading(false);
    })
  };

  const handleDelete = () => {
    // Abre o diálogo de confirmação em vez de executar direto
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {

    let payload = {
      nome: formData.nome,
      urlRef: formData.urlRef,
      emails: formData.emails,
      filtros: formData.filtros.map(filtro => ({
        nome: filtro.nome,
        tipo: filtro.tipo,
        prioridade: parseInt(filtro.prioridade) || 0,
        criteria: {
          inclusao: filtro.criteria?.inclusao || [],
          exclusao: filtro.criteria?.exclusao || []
        },
        targetColumn: filtro.targetColumn || null,
        sendSeparateEmail: filtro.sendSeparateEmail || false,
        outputSection: filtro.outputSection || ''
      })),
      active: formData.active
    };
    if (userData?.id) {
      payload.id = userData.id;
    }

    setLoading(true);
    Service.DELETE('/api/configUser', payload).then(resp => {
      setLoading(false);

      console.log(">>>DELETE<<<", resp)
      if (onSaveSuccess) onSaveSuccess();
      onClose();
      setConfirmDialogOpen(false)

    }).catch(error => {
      console.error(error);
      setLoading(false);
    })
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {isEditing ? 'Editar Configuração' : 'Nova Configuração'}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Campos básicos */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Assunto Email"
                value={formData.nome}
                onChange={handleChange('nome')}
                error={!!errors.nome}
                helperText={errors.nome}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="URL de Referência"
                value={formData.urlRef}
                onChange={handleChange('urlRef')}
                error={!!errors.urlRef}
                helperText={errors.urlRef}
                required
                placeholder="https://api.exemplo.com/endpoint"
                disabled
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12}>
              {/* <Divider sx={{ my: 2 }} /> */}
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Status
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    />
                  }
                  label={formData.active ? "Ativo" : "Inativo"}
                />
              </Box>
            </Grid>
            {/* Emails */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Emails de Notificação
              </Typography>
              <Box display="flex" gap={1} mb={2}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="email@exemplo.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddEmail();
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddEmail}
                  startIcon={<AddIcon />}
                >
                  Adicionar
                </Button>
              </Box>

              <Box sx={{ minHeight: 60, mb: 2 }}>
                {formData.emails.map((email, idx) => (
                  <Chip
                    key={idx}
                    label={email}
                    onDelete={() => handleRemoveEmail(email)}
                    sx={{ m: 0.5 }}
                  />
                ))}
                {formData.emails.length === 0 && (
                  <Typography color="textSecondary" variant="caption">
                    Nenhum email adicionado
                  </Typography>
                )}
              </Box>
              {errors.emails && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {errors.emails}
                </Alert>
              )}
            </Grid>


            {/* Filtros */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Filtros
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleAddFilter}
                  startIcon={<AddIcon />}
                  size="small"
                >
                  Adicionar Filtro
                </Button>
              </Box>

              {errors.filtros && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.filtros}
                </Alert>
              )}

              {formData.filtros.length === 0 ? (
                <Alert severity="info">
                  Nenhum filtro adicionado. Clique em "Adicionar Filtro" para começar.
                </Alert>
              ) : (
                formData.filtros.map((filter, index) => (
                  <Box
                    key={index}
                    ref={index === formData.filtros.length - 1 ? ultimoFiltroRef : null}
                  >
                    <FilterRuleEditor
                      key={index}
                      filter={filter}
                      index={index}
                      onUpdate={handleUpdateFilter}
                      onDelete={handleDeleteFilter}
                    /></Box>
                ))
              )}
            </Grid>

            {/* Erros gerais */}
            {errors.submit && (
              <Grid item xs={12}>
                <Alert severity="error">
                  {errors.submit}
                </Alert>
              </Grid>
            )}

            {/* Erros específicos de filtros */}
            {Object.keys(errors).filter(key => key.startsWith('filtro_')).map(errorKey => (
              <Grid item xs={12} key={errorKey}>
                <Alert severity="warning" sx={{ mb: 1 }}>
                  {errors[errorKey]}
                </Alert>
              </Grid>
            ))}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          {isEditing &&
            <>
              <Button
                onClick={handleDelete}
                variant="contained"
                color="error"
                disabled={loading}
              >
                Deletar
              </Button>
              <Button
                onClick={() => handleExport(userData)}
                variant="contained"
                color="primary"
                disabled={loading}
              >
                Exportar
              </Button>
            </>
          }
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Criar')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir "{formData.nome}"?
            Esta ação não poderá ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}